
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { HeartIcon, XIcon, CheckCircleIcon, SparklesIcon } from './icons';
import { useAppContext } from '../AppContext';
import { updateUserStylePreferences } from '@shared/services/userService';
import { CDN_URLS, OCI_FOLDERS } from '../config';

// Mock Data - In a real app, this would come from an API based on user segmentation
const SWIPE_ITEMS = [
    { id: 'swipe_1', imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_1.png`, tags: ['elegant', 'formal', 'minimalist'] },
    { id: 'swipe_2', imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Streetwear/street_1.png`, tags: ['streetwear', 'urban', 'casual'] },
    { id: 'swipe_3', imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Boho/boho_1.png`, tags: ['boho', 'relaxed', 'summer'] },
    { id: 'swipe_4', imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Business/business_1.png`, tags: ['business', 'professional', 'office'] },
    { id: 'swipe_5', imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Glamour/glam_1.png`, tags: ['glamour', 'party', 'bold'] },
];

const StyleSwipeWidget: React.FC = () => {
    const { t } = useTranslation();
    const { currentUserId, userProfile, setAllUsers } = useAppContext(); // Refresh context if needed
    const [currentIndex, setCurrentIndex] = useState(0);
    const [lastDirection, setLastDirection] = useState<'left' | 'right' | null>(null);
    const [isFinished, setIsFinished] = useState(false);

    // We only show this widget if the user hasn't completed it recently or at all.
    // For this demo, we'll just check if they've swiped all mock items in this session.

    const controls = useAnimation();

    const handleSwipe = async (direction: 'left' | 'right') => {
        setLastDirection(direction);

        await controls.start({
            x: direction === 'right' ? 500 : -500,
            opacity: 0,
            rotate: direction === 'right' ? 20 : -20,
            transition: { duration: 0.3 }
        });

        // Backend Update
        const item = SWIPE_ITEMS[currentIndex];
        // Optimistically update
        updateUserStylePreferences(currentUserId, item.tags, direction === 'right' ? 'like' : 'dislike');

        const nextIndex = currentIndex + 1;
        if (nextIndex < SWIPE_ITEMS.length) {
            setCurrentIndex(nextIndex);
            controls.set({ x: 0, opacity: 1, rotate: 0 }); // Reset for next card
        } else {
            setIsFinished(true);
        }
    };

    if (isFinished || userProfile.stylePreferences?.likedTags.length && userProfile.stylePreferences?.likedTags.length > 20) {
        // Don't show if finished or already has lots of data (simple rule for now)
        return (
            <div className="w-full max-w-md mx-auto p-6 bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-lg border border-gray-100 dark:border-[#C0A172]/20 text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('styleSwipe_finished_title') || "Style Profile Updated!"}</h3>
                <p className="text-gray-600 dark:text-gray-300">{t('styleSwipe_finished_desc') || "Your recommendations are now tuned to your unique taste."}</p>
            </div>
        );
    }

    const currentItem = SWIPE_ITEMS[currentIndex];

    return (
        <div className="w-full max-w-md mx-auto relative  h-[500px] perspective-1000">
            <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center pointer-events-none z-0">
                <div className="w-[90%] h-[90%] bg-gray-100 dark:bg-gray-800 rounded-3xl opacity-50 transform translate-y-4 scale-95"></div>
            </div>

            <motion.div
                animate={controls}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.8}
                onDragEnd={(e, { offset, velocity }) => {
                    const swipeThreshold = 100;
                    if (offset.x > swipeThreshold) {
                        handleSwipe('right');
                    } else if (offset.x < -swipeThreshold) {
                        handleSwipe('left');
                    }
                }}
                className="absolute top-0 left-0 w-full h-full bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden cursor-grab active:cursor-grabbing z-10 flex flex-col"
            >
                <div className="relative flex-grow overflow-hidden">
                    <img
                        src={currentItem.imageUrl}
                        alt="Style Option"
                        className="w-full h-full object-cover pointer-events-none"
                    />

                    {/* Gradients for text visibility */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none"></div>

                    <div className="absolute bottom-6 left-6 text-white pointer-events-none">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {currentItem.tags.map(tag => (
                                <span key={tag} className="px-2 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-wider border border-white/10">
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h3 className="text-2xl font-serif font-bold">Do you like this style?</h3>
                    </div>
                </div>

                <div className="p-6 flex justify-between items-center bg-white dark:bg-[#2A2A2A]">
                    <button
                        onClick={() => handleSwipe('left')}
                        className="w-14 h-14 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md flex items-center justify-center text-red-500 hover:scale-110 active:scale-95 transition-transform"
                    >
                        <XIcon className="w-6 h-6" />
                    </button>

                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">
                        {currentIndex + 1} / {SWIPE_ITEMS.length}
                    </span>

                    <button
                        onClick={() => handleSwipe('right')}
                        className="w-14 h-14 rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
                    >
                        <HeartIcon className="w-6 h-6" />
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default StyleSwipeWidget;
